import { useCallback, useEffect, useRef, useState } from 'react'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import DialogoDatos from '@/components/common/DialogoDatos'

/**
 * `window.confirm` y `window.prompt` como PROMESAS, pintados con `ConfirmDialog`.
 *
 * Las pantallas largas del asesor (cotización, ficha del cliente) encadenaban preguntas dentro de
 * funciones `async`: «¿hay datos? → ¿vaciar? → sembrar». Cambiar cada `window.confirm` por un
 * estado de diálogo propio partía esa cadena en tres manejadores. Así se conserva tal cual:
 *
 *   if (!(await confirmar({ titulo, descripcion }))) return
 *   const datos = await pedirDatos({ titulo, campos: [{ clave: 'motivo', etiqueta: 'Motivo' }] })
 *
 * `confirmar` resuelve `true`/`false`; `pedirDatos` resuelve `{ [clave]: texto }` o `null` si se
 * cancela. El componente que la use tiene que pintar `dialogo` una vez.
 */
export function useConfirmacion() {
  const [peticion, setPeticion] = useState(null)
  // El resolvedor vive en una ref: si el componente se desmonta con el diálogo abierto, la promesa
  // se resuelve como «cancelado» en vez de quedarse colgada para siempre.
  const resolver = useRef(null)

  const abrir = useCallback((tipo, opciones) => new Promise((res) => {
    resolver.current?.(tipo === 'confirmar' ? false : null)
    resolver.current = res
    setPeticion({ tipo, opciones })
  }), [])

  const cerrar = useCallback((valor) => {
    const r = resolver.current
    resolver.current = null
    setPeticion(null)
    r?.(valor)
  }, [])

  useEffect(() => () => { resolver.current?.(null) }, [])

  const confirmar = useCallback((opciones) => abrir('confirmar', opciones), [abrir])
  const pedirDatos = useCallback((opciones) => abrir('datos', opciones), [abrir])

  const o = peticion?.opciones || {}
  const dialogo = (
    <>
      <ConfirmDialog
        abierto={peticion?.tipo === 'confirmar'}
        onCerrar={() => cerrar(false)}
        onConfirmar={() => cerrar(true)}
        titulo={o.titulo || 'Confirmar'}
        descripcion={o.descripcion ? <div className="whitespace-pre-line">{o.descripcion}</div> : undefined}
        textoConfirmar={o.textoConfirmar}
        variante={o.variante}
        confirmacionEscrita={o.confirmacionEscrita}
      />
      <DialogoDatos
        abierto={peticion?.tipo === 'datos'}
        opciones={o}
        onCerrar={() => cerrar(null)}
        onEnviar={(valores) => cerrar(valores)}
      />
    </>
  )

  return { confirmar, pedirDatos, dialogo }
}

export default useConfirmacion
